/**
 * 🦸 AdminHeroes — owner-only Pet Tier stat editor.
 *
 * Live-edit HP / ATK / DEF / SPD / CRIT for each pet tier. Saves write
 * to mongo's `hero_overrides` collection and the next /arena/start picks
 * them up (30s cache — flush is automatic on save).
 *
 * Forward-compatible with Hero Classes (Knight/Mage/Archer/Rogue): the
 * backend already accepts a `hero_class` discriminator; this UI starts
 * with the `default` class and will gain a class picker once classes ship.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAT_META = {
  hp:   { label: "HP",   color: "text-emerald-300", step: 10, min: 1, max: 100000 },
  atk:  { label: "ATK",  color: "text-red-300",     step: 1,  min: 0, max: 100000 },
  def:  { label: "DEF",  color: "text-cyan-300",    step: 1,  min: 0, max: 100000 },
  spd:  { label: "SPD",  color: "text-amber-300",   step: 1,  min: 0, max: 100000 },
  crit: { label: "CRIT", color: "text-fuchsia-300", step: 1,  min: 0, max: 100 },
};

export default function AdminHeroes() {
  const { user, loading: authLoading } = useAuth();
  const [heroes, setHeroes] = useState(null);
  const [edits, setEdits] = useState({}); // { tier: { hp: 200, atk: ... } }
  const [busy, setBusy] = useState(false);

  const isOwner = user && (user.role === "owner" || (user.email || "").toLowerCase() === "omnimuscle@yahoo.com");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/heroes`);
      setHeroes(r.data.heroes);
      setEdits({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load heroes.");
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isOwner) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isOwner]);

  if (authLoading) return null;
  if (!isOwner) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400" data-testid="admin-heroes-locked">
        🔒 Owner only.
      </div>
    );
  }
  if (!heroes) {
    return <div className="max-w-3xl mx-auto p-8 text-center text-neutral-500">Loading…</div>;
  }

  const change = (tier, statKey, value) => {
    setEdits((prev) => ({
      ...prev,
      [tier]: { ...(prev[tier] || {}), [statKey]: value },
    }));
  };

  const save = async (h) => {
    const e = edits[h.tier];
    if (!e || !Object.keys(e).length) return;
    setBusy(true);
    try {
      await axios.put(`${API}/admin/heroes/${h.tier}`, {
        hero_class: h.hero_class,
        ...e,
      });
      toast.success(`✅ ${h.label} stats updated`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    }
    setBusy(false);
  };

  const reset = async (h) => {
    if (!window.confirm(`Reset ${h.label} (${h.hero_class}) to code defaults?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/heroes/${h.tier}`, {
        params: { hero_class: h.hero_class },
      });
      toast.success(`🔄 ${h.label} reset`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed.");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8" data-testid="admin-heroes-page">
      <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
        <a href="/admin/heroes" className="text-[#FCD34D] font-bold">🦸 Heroes</a>
        <span className="text-neutral-700">·</span>
        <a href="/admin/bosses" className="hover:text-white">👁️ Bosses</a>
      </div>
      <h1 className="font-display text-3xl mb-2">🦸 Hero Stat Editor</h1>
      <p className="text-xs text-neutral-500 mb-6 leading-relaxed">
        Tune pet tier stats live. Saves go straight to mongo and the next fight uses the new values.
        Cleared fields revert to code defaults. The <span className="text-[#FCD34D]">Hero Class</span> dropdown
        will activate when Knight / Mage / Archer / Rogue classes ship.
      </p>

      <div className="space-y-4">
        {heroes.map((h) => {
          const e = edits[h.tier] || {};
          return (
            <div
              key={`${h.tier}-${h.hero_class}`}
              className={`border p-4 ${h.is_overridden ? "border-[#FCD34D] bg-amber-950/10" : "border-neutral-800 bg-neutral-950"}`}
              data-testid={`admin-hero-${h.tier}`}
            >
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span className="text-3xl leading-none">{h.icon}</span>
                  <div>
                    <div className="font-display text-lg">{h.label}</div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      {h.tier} · class: {h.hero_class}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  {h.is_overridden && (
                    <span
                      className="text-[9px] uppercase tracking-widest text-[#FCD34D]"
                      title={h.overridden_fields.join(", ")}
                    >
                      🟡 Overridden ({h.overridden_fields.length})
                    </span>
                  )}
                  <button
                    onClick={() => save(h)}
                    disabled={busy || !Object.keys(e).length}
                    data-testid={`admin-hero-save-${h.tier}`}
                    className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#D4AF37] hover:bg-[#F1C84B] text-black disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Save
                  </button>
                  {h.is_overridden && (
                    <button
                      onClick={() => reset(h)}
                      disabled={busy}
                      data-testid={`admin-hero-reset-${h.tier}`}
                      className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-neutral-800 hover:bg-neutral-700 text-neutral-300"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {Object.entries(STAT_META).map(([k, meta]) => {
                  const dflt = h.defaults[k];
                  const cur = h.values[k];
                  const edited = e[k] !== undefined;
                  const display = edited ? e[k] : cur;
                  const overrides = h.overridden_fields.includes(k);
                  return (
                    <label key={k} className="flex flex-col gap-1">
                      <span className={`text-[10px] uppercase tracking-widest font-bold ${meta.color}`}>
                        {meta.label}
                        {overrides && !edited && (
                          <span className="text-[#FCD34D] ml-1" title="Has saved override">🟡</span>
                        )}
                      </span>
                      <input
                        type="number"
                        step={meta.step}
                        min={meta.min}
                        max={meta.max}
                        value={display ?? ""}
                        placeholder={String(dflt)}
                        onChange={(ev) => change(h.tier, k,
                          ev.target.value === "" ? null : Number(ev.target.value)
                        )}
                        data-testid={`admin-hero-${h.tier}-${k}`}
                        className={`w-full bg-black border px-2 py-1.5 text-sm text-white font-mono tabular-nums ${
                          edited ? "border-[#FCD34D]" : overrides ? "border-amber-700" : "border-neutral-800"
                        }`}
                      />
                      <span className="text-[9px] text-neutral-600 font-mono tabular-nums">
                        default: {dflt}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 text-[10px] text-neutral-600 leading-relaxed bg-neutral-950 border border-neutral-900 p-3">
        💡 <span className="text-neutral-400">Tip</span>: leave a field blank to revert that one stat to defaults.
        Use Reset to wipe all overrides for a tier. Changes are cached for 30s on the backend; subsequent fights
        will see the new numbers automatically.
      </div>
    </div>
  );
}
